import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const shouldReconnect = useRef(true);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      console.warn("[notifications] load failed:", e?.message || e);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setItems([]);
      return;
    }
    loadNotifications();
  }, [user, loadNotifications]);

  useEffect(() => {
    if (!user) return;
    shouldReconnect.current = true;
    const httpBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsBase = httpBase.replace(/^http/, "ws");
    // Same-origin WS handshake automatically forwards the httpOnly session cookie.
    const url = `${wsBase}/api/ws`;

    const connect = () => {
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.event === "notification" && msg.data) {
              setItems((prev) => [msg.data, ...prev]);
              toast.success(msg.data.title, {
                description: msg.data.message,
                icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
                duration: 8000,
              });
            }
          } catch (e) {
            console.warn("[notifications] bad ws frame:", e?.message || e);
          }
        };
        ws.onclose = (evt) => {
          wsRef.current = null;
          // 4401 = server closed for auth failure. No point reconnecting
          // until the user re-authenticates.
          if (shouldReconnect.current && evt?.code !== 4401) {
            reconnectTimer.current = setTimeout(connect, 4000);
          }
        };
        ws.onerror = () => {
          // Let onclose decide whether to reconnect. Forcing close() here
          // fires spurious "closed before established" warnings during the
          // React StrictMode double-mount and transient handshake hiccups.
        };
      } catch (e) {
        console.warn("[notifications] ws connect failed:", e?.message || e);
      }
    };
    connect();
    return () => {
      shouldReconnect.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          console.warn("[notifications] ws close on unmount failed:", e?.message || e);
        }
        wsRef.current = null;
      }
    };
  }, [user]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await api.post(`/notifications/${id}/read`);
    } catch (e) {
      console.warn("[notifications] markRead failed:", e?.message || e);
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.post("/notifications/read-all");
    } catch (e) {
      console.warn("[notifications] markAllRead failed:", e?.message || e);
    }
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider value={{ items, unread, markRead, markAllRead, refresh: loadNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext) || { items: [], unread: 0, markRead: () => {}, markAllRead: () => {} };
}
